import React from 'react';
import PropTypes from 'prop-types';
import { take } from 'lodash';
import { t } from '../../../../../modules/infra/i18n';
import { fullName } from '../../../../common/helpers/users';
import { getPhoto } from '../../../../common/helpers/photos';
import ReportBox from '../../components/ReportBox';
import Money from '../../../../common/money';
import Percentage from '../../../../common/Percentage';
import Num from '../../../../common/Num';
import defaultAvatar from '../../../../../../images/avatar.svg';

const Barber = ({ children, automationId }) => {
  if (children && children.length > 1) {
    return (
      <div className="c-leaderboard-barber">
        {take(children, 3).map(barber => (
          <div
            key={barber.id}
            className="c-leaderboard-barber__name"
            data-automation-id={`${automationId}:${barber.id}`}
          >
            {fullName(barber)}
          </div>
        ))}
        <div display-if={children.length > 3}>...</div>
      </div>
    );
  }

  return (
    <div className="c-leaderboard-barber">
      <div className="c-leaderboard-barber__img">
        <img src={(children && getPhoto(children[0])) || defaultAvatar} alt="" />
      </div>
      <div className="c-leaderboard-barber__name" data-automation-id={automationId}>
        {(children && fullName(children[0])) || '-'}
      </div>
    </div>
  );
};

Barber.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.number,
  ]),
  automationId: PropTypes.string,
};

Barber.defaultProps = {
  children: {},
  automationId: null,
};

const Leaderboard = ({
  serviceSales,
  productSales,
  productivity,
  averageSale,
  newCustomers,
  percentageThatIncludeAtLeastOneProduct,
}) => (
  <ReportBox title={t('reports:leaderboard.title')}>
    <div className="c-leaderboard u-text-center">

      <div className="c-leaderboard__item">
        <div className="u-flex-1">
          <div className="c-report-label u-type--uppercase">{t('reports:leaderboard.service_sales')}</div>
          <div className="c-report-stat">
            <Money precision={0} automationId="text:leader:serviceSalesValue">{serviceSales.value || 0}</Money>
          </div>
        </div>
        <div className="c-vertical-divider" />
        <Barber automationId="text:leader:serviceSalesBarber">{serviceSales.value && serviceSales.barber}</Barber>
      </div>

      <div className="c-leaderboard__item">
        <div className="u-flex-1">
          <div className="c-report-label u-type--uppercase">{t('reports:leaderboard.product_sales')}</div>
          <div className="c-report-stat">
            <Money precision={0} automationId="text:leader:productSalesValue">{productSales.value || 0}</Money>
          </div>
        </div>
        <div className="c-vertical-divider" />
        <Barber automationId="text:leader:productSalesBarber">{productSales.value && productSales.barber}</Barber>
      </div>

      <div className="c-leaderboard__item">
        <div className="u-flex-1">
          <div className="c-report-label u-type--uppercase">{t('reports:leaderboard.productivity')}</div>
          <div className="c-report-stat">
            <Percentage precision={0} automationId="text:leader:productivityValue">
              {productivity.value || 0}
            </Percentage>
          </div>
        </div>
        <div className="c-vertical-divider" />
        <Barber automationId="text:leader:productivityBarber">{productivity.value && productivity.barber}</Barber>
      </div>

      <div className="c-leaderboard__item">
        <div className="u-flex-1">
          <div className="c-report-label u-type--uppercase">{t('reports:leaderboard.average_ticket')}</div>
          <div className="c-report-stat">
            <Money precision={0} automationId="text:leader:averageTicketValue">{averageSale.value || 0}</Money>
          </div>
        </div>
        <div className="c-vertical-divider" />
        <Barber automationId="text:leader:averageTicketBarber">{averageSale.value && averageSale.barber}</Barber>
      </div>

      <div className="c-leaderboard__item">
        <div className="u-flex-1">
          <div className="c-report-label u-type--uppercase">{t('reports:leaderboard.new_clients')}</div>
          <div className="c-report-stat">
            <Num automationId="text:leader:newClientsValue">{newCustomers.value || 0}</Num>
          </div>
        </div>
        <div className="c-vertical-divider" />
        <Barber automationId="text:leader:newClientsBarber">{newCustomers.value && newCustomers.barber}</Barber>
      </div>

      <div className="c-leaderboard__item">
        <div className="u-flex-1">
          <div className="c-report-label u-type--uppercase">{t('reports:leaderboard.tickets_with_product')}</div>
          <div className="c-report-stat">
            <Percentage precision={0} automationId="text:leader:ticketsWithProductValue">
              {percentageThatIncludeAtLeastOneProduct.value || 0}
            </Percentage>
          </div>
        </div>
        <div className="c-vertical-divider" />
        <Barber automationId="text:leader:ticketsWithProductBarber">
          {percentageThatIncludeAtLeastOneProduct.value && percentageThatIncludeAtLeastOneProduct.barber}
        </Barber>
      </div>
    </div>
  </ReportBox>
);
Leaderboard.propTypes = {
  serviceSales: PropTypes.shape({
    barber: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.number,
  }).isRequired,
  productSales: PropTypes.shape({
    barber: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.number,
  }).isRequired,
  productivity: PropTypes.shape({
    barber: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.number,
  }).isRequired,
  averageSale: PropTypes.shape({
    barber: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.number,
  }).isRequired,
  newCustomers: PropTypes.shape({
    barber: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.number,
  }).isRequired,
  percentageThatIncludeAtLeastOneProduct: PropTypes.shape({
    barber: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.number,
  }).isRequired,
};

export default Leaderboard;
